import { AlertCircle, CalendarX } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { EventMeta } from "components/EventMeta";
import { UnpublishedBadge } from "components/EventSummaryCard";
import { EmptyState, LoadingCards } from "components/states";
import { ToggleChip } from "components/ToggleChip";
import { Card, CardContent } from "components/ui/card";

import { ArchivedChip, EndedChip } from "../dashboard/EventTileParts";
import { useNow } from "../dashboard/use-today";
import {
  eventHasEnded,
  splitArchived,
  splitPast,
  type EventTiming,
} from "../events/event-past";
import { PastSection } from "../events/PastSection";

import {
  EventArchiveControl,
  type ArchiveOutcome,
} from "../events/EventArchiveControl";
import type { EventArchiveCommand } from "../events/use-event-archive";
import { EventDeletionControl } from "../events/EventDeletionControl";
import { describeReadFailure } from "./describe-read-failure";
import type { GovernanceEvent } from "./schemas";
import type { GovernanceEventsState } from "./use-governance-events";

/**
 * Every event on the platform, each with its archive and delete controls.
 *
 * Archiving follows the same rule as deleting: the
 * `event_archive_requests` create rule and the archive handler accept
 * `super_admin` in place of event admin, so a super admin can archive or
 * restore any event from here.
 *
 * A super admin may delete any event: the `event_deletion_requests` create rule
 * and the DELETE_EVENT handoff both accept `super_admin` in place of event
 * admin. Before this list the only super-admin entry point was the danger zone
 * under the Event Admins form, visible once an event had been picked there, so
 * an event nobody had made her admin of looked undeletable. Deleting from here
 * files the same request as the event's own settings page, so the lock, the
 * unpublish and the asynchronous job are the same too.
 */
export function GovernanceEventsSection({
  uid,
  events,
}: {
  uid: string;
  events: GovernanceEventsState & { reload: () => void };
}) {
  // Held here, above the rows: the reload that follows a deletion removes the
  // deleted event's row, and its own "Event deleted." line with it.
  const [deleted, setDeleted] = useState<string[]>([]);
  // Held here for the same reason: archiving moves the event into the hidden
  // Archived list, and restoring moves it back out, so the row that filed the
  // request is gone once the reload lands, with its status line.
  const [archiveNotes, setArchiveNotes] = useState<string[]>([]);
  // Requests whose wait timed out, by event. They can still go through, and
  // the reload that follows the timeout may show it, but that reload moves
  // the event to the other list and unmounts the row that filed it. So the
  // section compares its own read with what was asked.
  const [timedOut, setTimedOut] = useState<Record<string, EventArchiveCommand>>(
    {},
  );

  const now = useNow();
  const [showArchived, setShowArchived] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);
  const data = events.status === "success" ? events.data : null;
  const { active, archived } = useMemo(
    () => splitArchived(data ?? [], eventTiming),
    [data],
  );
  const split = useMemo(
    () => splitPast(active, eventTiming, now, { showAll: showAllPast }),
    [active, now, showAllPast],
  );

  useEffect(() => {
    const applied = (data ?? []).filter((event) => {
      const command = timedOut[event.eventId];
      return (
        command !== undefined &&
        (event.archived === true) === (command === "ARCHIVE_EVENT")
      );
    });
    if (applied.length === 0) return;
    setArchiveNotes((notes) => [
      ...notes,
      ...applied.map((event) =>
        archiveNote(event.title ?? event.eventId, {
          command: timedOut[event.eventId],
          superseded: false,
        }),
      ),
    ]);
    setTimedOut((pending) => withoutEvents(pending, applied));
  }, [data, timedOut]);

  // Both regions are always in the page, empty when there is nothing to
  // say, so a screen reader is already listening when the first note lands.
  // An element added together with its text is often not announced, and a
  // hidden one is not announced at all, so they are empty rather than hidden.
  const status = (
    <div className="space-y-1">
      <p
        className="text-sm text-foreground"
        role="status"
        data-testid="governance-events-deleted"
      >
        {deleted.length > 0 ? `Deleted ${deleted.join(", ")}.` : ""}
      </p>
      <p
        className="text-sm text-foreground"
        role="status"
        data-testid="governance-events-archived"
      >
        {archiveNotes.join(" ")}
      </p>
    </div>
  );

  if (events.status === "loading") return <LoadingCards cards={3} />;
  if (events.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load events">
        {describeReadFailure(events.error)}
      </EmptyState>
    );
  if (events.data.length === 0)
    return (
      <div className="space-y-3">
        {status}
        <EmptyState icon={CalendarX} title="No events yet">
          Events you or an event admin create appear here.
        </EmptyState>
      </div>
    );

  const row = (event: GovernanceEvent, ended = false) => (
    <GovernanceEventRow
      key={event.eventId}
      uid={uid}
      event={event}
      ended={ended}
      onDeleted={(title) => {
        setDeleted((titles) => [...titles, title]);
        events.reload();
      }}
      onArchiveChanged={events.reload}
      onArchived={(title, outcome) => {
        setArchiveNotes((notes) => [...notes, archiveNote(title, outcome)]);
        // A later request for the same event decided it; an earlier one that
        // timed out has nothing left to report.
        setTimedOut((pending) => withoutEvents(pending, [event]));
      }}
      onArchiveTimedOut={(command) =>
        setTimedOut((pending) => ({ ...pending, [event.eventId]: command }))
      }
    />
  );

  // Not offered when nothing is archived, but kept while on so it can be
  // turned off, which matters right after the last archived event is deleted.
  const chip =
    archived.length > 0 || showArchived ? (
      <ToggleChip
        selected={showArchived}
        onClick={() => setShowArchived((shown) => !shown)}
        data-testid="governance-archived-filter"
      >
        Archived ({archived.length})
      </ToggleChip>
    ) : null;

  if (showArchived) {
    return (
      <div className="space-y-3" data-testid="governance-events-list">
        {status}
        {chip}
        {archived.length === 0 ? (
          <EmptyState icon={CalendarX} title="No archived events">
            Events an organiser archives appear here.
          </EmptyState>
        ) : (
          <div className="space-y-3" data-testid="governance-archived-list">
            {archived.map((event) => row(event))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="governance-events-list">
      {status}
      {chip}
      {split.current.map((event) => row(event))}
      {split.current.length === 0 && split.past.length === 0 && (
        <EmptyState icon={CalendarX} title="All events are archived">
          Use the Archived filter to see them.
        </EmptyState>
      )}
      {split.past.length > 0 && (
        <PastSection
          total={split.pastTotal}
          hasMore={split.hasMorePast}
          expanded={showAllPast}
          onToggle={() => setShowAllPast((shown) => !shown)}
          testId="governance-past"
        >
          <div className="space-y-3" data-testid="governance-past-list">
            {split.past.map((event) =>
              row(event, eventHasEnded(eventTiming(event), now)),
            )}
          </div>
        </PastSection>
      )}
    </div>
  );
}

/** What the list says above its rows once an archive request went through. */
function archiveNote(title: string, outcome: ArchiveOutcome): string {
  if (outcome.superseded) return `A newer request decided ${title}'s state.`;
  return outcome.command === "ARCHIVE_EVENT"
    ? `Archived ${title}.`
    : `Restored ${title}.`;
}

function withoutEvents(
  pending: Record<string, EventArchiveCommand>,
  events: GovernanceEvent[],
): Record<string, EventArchiveCommand> {
  const next = { ...pending };
  for (const event of events) delete next[event.eventId];
  return next;
}

const eventTiming = (event: GovernanceEvent): EventTiming => ({
  startDate: event.startDate,
  endDate: event.endDate,
  timezone: event.timezone,
  archived: event.archived,
});

function GovernanceEventRow({
  uid,
  event,
  ended,
  onDeleted,
  onArchiveChanged,
  onArchived,
  onArchiveTimedOut,
}: {
  uid: string;
  event: GovernanceEvent;
  ended: boolean;
  onDeleted: (title: string) => void;
  onArchiveChanged: () => void;
  onArchived: (title: string, outcome: ArchiveOutcome) => void;
  onArchiveTimedOut: (command: EventArchiveCommand) => void;
}) {
  const title = event.title ?? event.eventId;
  return (
    <Card className="p-4" data-testid={`governance-event-${event.eventId}`}>
      <CardContent className="flex flex-col gap-3 pt-0 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 wrap-anywhere">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-foreground">{title}</p>
            {/* An event with no published field is not published: the
                projection and isValidEvent both read absent as false. */}
            {event.published !== true && <UnpublishedBadge />}
            {ended && <EndedChip />}
            {event.archived === true && <ArchivedChip />}
          </div>
          <p className="font-mono text-xs text-muted-foreground">
            {event.eventId}
          </p>
          <EventMeta
            startDate={event.startDate}
            endDate={event.endDate}
            className="mt-1 text-xs"
          />
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:items-end">
          <EventArchiveControl
            eventId={event.eventId}
            eventTitle={title}
            uid={uid}
            archived={event.archived === true}
            onChanged={onArchiveChanged}
            onSucceeded={(outcome) => onArchived(title, outcome)}
            onTimedOut={onArchiveTimedOut}
            // The section's note above the rows announces the outcome; the
            // row's own line would say it a second time.
            announceOutcome={false}
          />
          <EventDeletionControl
            eventId={event.eventId}
            eventTitle={title}
            uid={uid}
            onSucceeded={() => onDeleted(title)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
